import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { GiConfigurationForAddNewViewTab } from "../../../../data-model/giConfigDetails";
import { GiConfiguration } from "../../../../pages/configurations/pages/giRpaConfiguration/hooks/useGiConfiguration";
import { RootState } from "../../../../redux/store/store";

/**
 * Custom hook that manages local GI configuration state for the Add/Edit View Tab modal.
 *
 * This hook handles:
 * - Syncing configuration state from Redux modal metadata when editing an existing configuration
 * - Managing the editable JSON text representation
 * - Tracking JSON validation errors for inline feedback in the editor
 * - Detecting changes by comparing current text against the original configuration
 *
 * @returns {Object} The hook's state and setters
 * @returns {GiConfigurationForAddNewViewTab} giConfiguration - The current GI configuration object
 * @returns {Dispatch<SetStateAction<GiConfigurationForAddNewViewTab>>} setGiConfiguration - Setter for the configuration object
 * @returns {string} configText - The raw JSON string displayed in the textarea editor
 * @returns {Dispatch<SetStateAction<string>>} setConfigText - Setter for the raw JSON text
 * @returns {string | null} configError - JSON parsing error message, or null if valid
 * @returns {Dispatch<SetStateAction<string | null>>} setConfigError - Setter for the error message
 * @returns {boolean} isConfigChanged - Whether the current configText differs from the original configuration
 *
 * @example
 * const {
 *   giConfiguration,
 *   setGiConfiguration,
 *   configText,
 *   setConfigText,
 *   configError,
 *   setConfigError,
 *   isConfigChanged,
 * } = useGiConfigDetails();
 *
 * // Show warning when config has been modified
 * {isConfigChanged && <span>Unsaved changes</span>}
 */
export const useGiConfigDetails = () => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const selectedGiConfiguration =
    metaData?.selectedGiConfiguration as GiConfiguration | null;
  const [giConfiguration, setGiConfiguration] =
    useState<GiConfigurationForAddNewViewTab>({
      config: {},
      num_orders: 0,
      view_tab: "",
      id: "",
      created_at: "",
      created_by: "",
      version: "",
    });
  const [configText, setConfigText] = useState<string>(
    JSON.stringify(giConfiguration.config ?? {}, null, 2),
  );
  const [originalConfigText, setOriginalConfigText] = useState<string>("{}");
  const [isConfigChanged, setIsConfigChanged] = useState<boolean>(false);
  const [configError, setConfigError] = useState<string | null>(null);

  /**
   * Initializes local state when a configuration is selected from modal metadata.
   */
  useEffect(() => {
    if (selectedGiConfiguration) {
      const configString = JSON.stringify(
        selectedGiConfiguration.config ?? {},
        null,
        2,
      );
      setGiConfiguration((prev) => ({
        ...prev,
        id: selectedGiConfiguration.id || "",
        view_tab: selectedGiConfiguration.view_tab || "",
        num_orders: selectedGiConfiguration.num_orders || 0,
        config: selectedGiConfiguration.config || {},
        version: selectedGiConfiguration.version || "",
      }));
      setConfigText(configString);
      setOriginalConfigText(configString);
      setConfigError(null);
    }
  }, [selectedGiConfiguration]);

  /**
   * Compares current configText against the original to detect changes.
   */
  useEffect(() => {
    const hasChanged = originalConfigText !== configText;
    setIsConfigChanged(hasChanged);
  }, [configText, originalConfigText]);

  const isRequiredFieldsFilledAndValid = useMemo(() => {
    const isConfigNonEmpty =
      Object.keys(giConfiguration.config ?? {}).length > 0;
    const isNumOrdersValid =
      giConfiguration.num_orders !== undefined &&
      giConfiguration.num_orders !== null &&
      giConfiguration.num_orders >= 0;
    const isViewTabValid =
      giConfiguration.view_tab !== undefined &&
      giConfiguration.view_tab !== null &&
      giConfiguration.view_tab.trim() !== "";
    return isConfigNonEmpty && isNumOrdersValid && isViewTabValid;
  }, [giConfiguration]);

  return {
    giConfiguration,
    setGiConfiguration,
    configText,
    setConfigText,
    configError,
    setConfigError,
    isConfigChanged,
    isRequiredFieldsFilledAndValid,
  };
};
